import { motion, useInView } from "framer-motion";
import { useRef } from "react";

const timeline = [
  {
    year: "2020",
    title: "Inception",
    description:
      "Impact Together was founded with a mission to bridge the gap between strategy and on-ground implementation.",
    image: "ideation.svg",
  },
  {
    year: "2021 - 2023",
    title: "Grassroots Engagement & NGO Capacity Building",
    description:
      "Strengthened the operational and strategic capacities of over 10+ grassroots NGOs, equipping them with systems, tools, and frameworks to scale their impact. Partnered with 14+ government bodies, including Nagar Parishads, Nagar Panchayats, and Nagar Palikas, supporting them in scheme implementation, community outreach, and monitoring processes.",
    image: "partnership.svg",
  },
  {
    year: "2024",
    title: "Geographic Expansion",
    description:
      "Expanded services to 5+ districts, deepening our engagement across rural and semi-urban areas and supporting multi-stakeholder collaborations. Today, we stand as a growing ecosystem enabler, bringing together experience, empathy, and execution to create meaningful and measurable change.",
    image: "programs.svg",
  },
];

const JourneyTimeline = () => {
  const stepRef0 = useRef(null);
  const stepRef1 = useRef(null);
  const stepRef2 = useRef(null);
  const stepRef3 = useRef(null);

  const inView0 = useInView(stepRef0, { once: false, amount: 0.5 });
  const inView1 = useInView(stepRef1, { once: false, amount: 0.5 });
  const inView2 = useInView(stepRef2, { once: false, amount: 0.5 });
  const inView3 = useInView(stepRef3, { once: false, amount: 0.5 });

  const stepRefs = [stepRef0, stepRef1, stepRef2, stepRef3];
  const inViews = [inView0, inView1, inView2, inView3];

  return (
    <section className="py-16 bg-gradient-to-br from-white via-blue-50 to-purple-50">
      <div className="max-w-6xl mx-auto px-4">
        <div className="mb-16 text-center">
          <h2 className="text-4xl md:text-5xl font-heading font-extrabold text-primary mb-4 tracking-tight drop-shadow-sm inline-block relative">
            Our Journey
            <span className="block h-1 w-1/2 md:w-1/4 mx-auto bg-gradient-to-r from-primary via-secondary to-pink-400 rounded-full mt-3" />
          </h2>
          {/* Intro Section */}
          <motion.div
            className="max-w-4xl mx-auto mb-16 text-center px-4 md:px-0"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.5 }}
            transition={{ duration: 0.8 }}
          >
            <h3 className="text-base md:text-lg font-semibold uppercase tracking-widest text-secondary mb-2">
              From Local Partnerships to Systemic Impact
            </h3>
            <p className="text-gray-700 text-md md:text-lg leading-relaxed font-medium">
              Impact Together Consultancy Services began its journey in{" "}
              <span className="text-primary font-bold">2020</span> with a simple
              yet powerful vision — to enable{" "}
              <span className="text-secondary font-semibold">
                collaborative
              </span>
              ,{" "}
              <span className="text-secondary font-semibold">sustainable</span>,
              and{" "}
              <span className="text-secondary font-semibold">
                grassroots-driven
              </span>{" "}
              development. What started as a small consulting initiative has
              grown into a trusted partner for governments, non-profits, and CSR
              institutions across Maharashtra and beyond.
            </p>
          </motion.div>
        </div>

        <div className="relative">
          {/* Vertical Line */}
          <div className="hidden md:block absolute left-[40%] top-0 bottom-0 w-1.5 z-0">
            <motion.div
              className="w-1.5 h-full bg-gradient-to-b from-primary via-secondary to-pink-400 opacity-80"
              initial={{ scaleY: 0 }}
              whileInView={{ scaleY: 1 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 1.2, ease: "easeInOut" }}
              style={{ transformOrigin: "top" }}
            />
          </div>

          {/* Timeline Steps */}
          {timeline.map((step, index) => {
            const stepRef = stepRefs[index];
            const inView = inViews[index];
            return (
              <motion.div
                key={index}
                ref={stepRef}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: false, amount: 0.5 }}
                transition={{
                  delay: index * 0.12,
                  duration: 0.6,
                  type: "spring",
                }}
                className="mb-12 flex flex-col md:flex-row items-center md:items-start gap-6 md:gap-14 px-2"
              >
                {/* SVG Illustration (left) */}
                <motion.div
                  className="w-full md:w-2/5 flex justify-center md:justify-end"
                  initial={{ scale: 0.95, opacity: 0 }}
                  whileInView={{ scale: 1, opacity: 1 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                >
                  <img
                    src={`${process.env.PUBLIC_URL}/assets/journey/${step.image}`}
                    alt={step.title}
                    className="w-56 md:w-64 lg:w-72 max-w-full drop-shadow-xl h-auto"
                    loading="lazy"
                  />
                </motion.div>

                {/* Text + timeline dot (right) */}
                <div className="w-full md:w-3/5 relative pl-8 md:pl-10 border-l-2 border-primary/20">
                  {/* Timeline Dot */}
                  <div className="absolute -left-4 md:-left-[26px] top-2 w-6 h-6 rounded-full bg-gradient-to-br from-primary via-secondary to-pink-400 shadow-md border-4 border-white" />

                  {/* Card Content */}
                  <motion.div
                    className={`bg-white/90 backdrop-blur-md p-4 md:p-6 rounded-xl shadow-md transition-all duration-300 ${
                      inView ? "ring-2 ring-primary/30 scale-[1.02]" : ""
                    }`}
                  >
                    <h3 className="text-lg md:text-xl font-bold text-primary mb-1">
                      {step.year}{" "}
                      <span className="text-gray-400 font-normal">–</span>{" "}
                      <span className="text-secondary">{step.title}</span>
                    </h3>
                    <p className="text-gray-600 text-sm md:text-base leading-relaxed">
                      {step.description}
                    </p>
                  </motion.div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default JourneyTimeline;
